/**
 * Employee Incidents page — /employee/incidents.
 *
 * Lets any employee file an incident report and track all their past
 * reports along with the full admin timeline (status transitions,
 * forwards, resolution and admin/assignee comments).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Siren, Clock, CheckCircle, XCircle, Warning,
  Fire, Flame, Tag, Plus, Paperclip, Download, PaperPlaneTilt,
  ChatCircleText, X, CaretLeft,
} from '@phosphor-icons/react';

import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = '/branding/logo.png';

const STATUS_META = {
  submitted:    { ar: 'جديد',          en: 'New',         cls: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-800',    Icon: Siren },
  under_review: { ar: 'قيد المراجعة',  en: 'Under review',cls: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/40 dark:text-amber-200 dark:border-amber-800',  Icon: Clock },
  assigned:     { ar: 'محوّل',         en: 'Forwarded',   cls: 'bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-900/40 dark:text-indigo-200 dark:border-indigo-800',Icon: PaperPlaneTilt },
  in_progress:  { ar: 'قيد التنفيذ',   en: 'In progress', cls: 'bg-violet-100 text-violet-800 border-violet-200 dark:bg-violet-900/40 dark:text-violet-200 dark:border-violet-800',Icon: Clock },
  resolved:     { ar: 'تم اتخاذ إجراء',en: 'Resolved',    cls: 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-800',Icon: CheckCircle },
  closed:       { ar: 'مغلق',           en: 'Closed',      cls: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',   Icon: CheckCircle },
  rejected:     { ar: 'مرفوض',          en: 'Rejected',    cls: 'bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-900/40 dark:text-rose-200 dark:border-rose-800',      Icon: XCircle },
};

const PRIORITY_META = {
  urgent: { ar: 'عاجل',   en: 'Urgent', cls: 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/40 dark:text-red-200 dark:border-red-800',   Icon: Fire },
  high:   { ar: 'مرتفع',  en: 'High',   cls: 'bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900/40 dark:text-orange-200 dark:border-orange-800', Icon: Flame },
  medium: { ar: 'متوسط',  en: 'Medium', cls: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/40 dark:text-amber-200 dark:border-amber-800', Icon: Warning },
  low:    { ar: 'منخفض',  en: 'Low',    cls: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700', Icon: Tag },
};

const EVENT_LABELS = {
  submitted:          { ar: 'تم تقديم البلاغ', en: 'Report submitted' },
  under_review:       { ar: 'بدأت المراجعة',    en: 'Review started' },
  forwarded:          { ar: 'تم التحويل',       en: 'Forwarded' },
  'status:in_progress': { ar: 'قيد التنفيذ',    en: 'In progress' },
  'status:resolved':   { ar: 'تم اتخاذ إجراء',   en: 'Resolved' },
  'status:closed':     { ar: 'تم الإغلاق',       en: 'Closed' },
  'status:rejected':   { ar: 'تم الرفض',         en: 'Rejected' },
  comment:             { ar: 'تعليق جديد',       en: 'Comment added' },
};

const MAX_ATT = 3;
const MAX_BYTES = 10 * 1024 * 1024;

export default function EmployeeIncidents() {
  const navigate = useNavigate();
  const { language, direction } = useLanguage();
  const isAr = language === 'ar';
  const T = (ar, en) => (isAr ? ar : en);
  const [params, setParams] = useSearchParams();

  const [incidents, setIncidents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    category_key: '', priority: 'medium', title: '', description: '',
    incident_date: new Date().toISOString().slice(0, 10),
    location: '', witnesses: '',
  });
  const [attachments, setAttachments] = useState([]);        // {filename, content_type, data_url}
  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [commenting, setCommenting] = useState(false);

  const fetchMine = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/incidents/mine`, { withCredentials: true });
      setIncidents(res.data.incidents || []);
    } catch (e) { toast.error(T('تعذّر تحميل البلاغات', 'Failed to load')); }
    finally { setLoading(false); }
  }, [isAr]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/incidents/categories`, { withCredentials: true });
      setCategories((res.data.categories || []).filter(c => c.active));
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => { fetchMine(); fetchCategories(); }, [fetchMine, fetchCategories]);

  // Deep-link ?focus=<id>
  useEffect(() => {
    const focus = params.get('focus');
    if (focus && focus !== detailId) setDetailId(focus);
  }, [params, detailId]);

  const openDetail = useCallback(async (id) => {
    setDetailId(id);
    try {
      const res = await axios.get(`${API}/incidents/${id}`, { withCredentials: true });
      setDetail(res.data);
    } catch (e) {
      toast.error(T('تعذّر عرض البلاغ', 'Failed to open'));
      setDetailId(null);
    }
  }, [isAr]);

  useEffect(() => { if (detailId) openDetail(detailId); }, [detailId, openDetail]);

  const closeDetail = () => {
    setDetailId(null); setDetail(null); setCommentText('');
    const next = new URLSearchParams(params);
    next.delete('focus');
    setParams(next, { replace: true });
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (attachments.length + files.length > MAX_ATT) {
      toast.error(T(`الحد الأقصى ${MAX_ATT} مرفقات`, `Max ${MAX_ATT} attachments`));
      return;
    }
    files.forEach((f) => {
      if (f.size > MAX_BYTES) { toast.error(T(`${f.name} أكبر من 10MB`, `${f.name} is larger than 10MB`)); return; }
      const reader = new FileReader();
      reader.onload = () => {
        setAttachments((prev) => [...prev, {
          filename: f.name,
          content_type: f.type || 'application/octet-stream',
          data_url: reader.result,
          size: f.size,
        }]);
      };
      reader.readAsDataURL(f);
    });
    e.target.value = '';
  };

  const removeAttachment = (idx) => setAttachments((prev) => prev.filter((_, i) => i !== idx));

  const submitIncident = async () => {
    if (!form.category_key) { toast.error(T('اختر تصنيف البلاغ', 'Select a category')); return; }
    if (form.title.trim().length < 5) { toast.error(T('العنوان قصير جداً (5 حروف على الأقل)', 'Title too short')); return; }
    if (form.description.trim().length < 10) { toast.error(T('الوصف قصير جداً (10 حروف على الأقل)', 'Description too short')); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        attachments: attachments.map(a => ({
          filename: a.filename, content_type: a.content_type, data_url: a.data_url,
        })),
      };
      const res = await axios.post(`${API}/incidents`, payload, { withCredentials: true });
      toast.success(T(`تم تقديم بلاغك: ${res.data.reference}`, `Submitted: ${res.data.reference}`));
      setShowNew(false);
      setForm({
        category_key: '', priority: 'medium', title: '', description: '',
        incident_date: new Date().toISOString().slice(0, 10), location: '', witnesses: '',
      });
      setAttachments([]);
      await fetchMine();
    } catch (e) {
      toast.error(e?.response?.data?.detail || T('فشل الإرسال', 'Submit failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const postComment = async () => {
    if (!commentText.trim()) return;
    setCommenting(true);
    try {
      await axios.post(`${API}/incidents/${detailId}/comment`, { text: commentText.trim() }, { withCredentials: true });
      setCommentText('');
      await openDetail(detailId);
      await fetchMine();
    } catch (e) {
      toast.error(T('تعذّر إرسال التعليق', 'Comment failed'));
    } finally {
      setCommenting(false);
    }
  };

  const fmt = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(isAr ? 'ar-SA' : 'en-GB',
      { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
  };

  const inc = detail?.incident;
  const events = detail?.events || [];
  const sm = inc ? (STATUS_META[inc.status] || STATUS_META.submitted) : null;
  const pm = inc ? (PRIORITY_META[inc.priority] || PRIORITY_META.medium) : null;

  return (
    <div className="min-h-screen bg-[#F2F0FA] dark:bg-slate-950" dir={direction} data-testid="employee-incidents-page">
      {/* Header — standard employee page header: logo + Back */}
      <header className="sticky top-0 z-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm shadow-sm border-b border-[#C4BFF0] dark:border-slate-700 py-4">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
          <Button variant="ghost" onClick={() => navigate('/employee/dashboard')} className="text-[#655dc6] hover:bg-[#EDE9FE] dark:hover:bg-slate-800" data-testid="back-btn">
            <CaretLeft size={18} className={isAr ? 'ms-1 rotate-180' : 'me-1'} />
            {T('رجوع', 'Back')}
          </Button>
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Title + new report */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-[#655dc6] dark:text-violet-300 flex items-center gap-2">
              <Siren size={28} weight="duotone" className="text-rose-600 dark:text-rose-400" />
              {T('بلاغاتي', 'My Incidents')}
            </h1>
            <p className="text-sm text-[#655dc6]/70 dark:text-slate-400 mt-1">
              {T('قدّم بلاغ حادث أو شكوى وتتبّع مراحل المعالجة لحظياً.', 'File an incident report and watch its progress in real time.')}
            </p>
          </div>
          <Button
            onClick={() => setShowNew(true)}
            className="bg-rose-600 hover:bg-rose-700 text-white gap-2 shrink-0"
            data-testid="new-incident-btn"
          >
            <Plus size={18} weight="bold" />
            {T('بلاغ جديد', 'New report')}
          </Button>
        </div>
        <Card className="border-[#C4BFF0] dark:border-slate-700 dark:bg-slate-900">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#655dc6] dark:text-violet-300">
              {incidents.length} {T('بلاغ', 'reports')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#655dc6]" />
              </div>
            ) : incidents.length === 0 ? (
              <div className="text-center py-12 text-[#655dc6]/60 dark:text-slate-400 text-sm">
                <Siren size={36} weight="duotone" className="mx-auto mb-2 text-[#655dc6]/40 dark:text-slate-500" />
                <p>{T('لم تقدّم أي بلاغ بعد', 'You have no reports yet')}</p>
                <Button onClick={() => setShowNew(true)} className="mt-4 bg-rose-600 hover:bg-rose-700 text-white gap-1" data-testid="first-report-btn">
                  <Plus size={16} /> {T('ابدأ أول بلاغ', 'File your first report')}
                </Button>
              </div>
            ) : (
              <div className="space-y-3" data-testid="my-incidents-list">
                {incidents.map((i) => {
                  const sm2 = STATUS_META[i.status] || STATUS_META.submitted;
                  const pm2 = PRIORITY_META[i.priority] || PRIORITY_META.medium;
                  const StatusIcon = sm2.Icon; const PrIcon = pm2.Icon;
                  return (
                    <button
                      key={i.id}
                      onClick={() => openDetail(i.id)}
                      className="w-full text-start bg-white dark:bg-slate-900 border border-[#C4BFF0] dark:border-slate-700 rounded-xl p-4 hover:border-rose-400 hover:shadow-sm transition"
                      data-testid={`my-incident-${i.id}`}
                    >
                      <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
                        <span className="font-mono text-[11px] text-[#655dc6]/80 dark:text-slate-300 bg-[#E8E6F5] dark:bg-slate-800 border border-[#C4BFF0] dark:border-slate-700 px-1.5 py-0.5 rounded">{i.reference}</span>
                        <Badge variant="outline" className={`${sm2.cls} text-[10px] gap-1`}>
                          <StatusIcon size={10} weight="bold" />{T(sm2.ar, sm2.en)}
                        </Badge>
                        <Badge variant="outline" className={`${pm2.cls} text-[10px] gap-1`}>
                          <PrIcon size={10} weight="bold" />{T(pm2.ar, pm2.en)}
                        </Badge>
                        <Badge variant="outline" className="bg-[#E8E6F5] dark:bg-slate-800 text-[#655dc6] dark:text-violet-300 border-[#C4BFF0] dark:border-slate-700 text-[10px]">
                          {T(i.category?.name_ar, i.category?.name_en)}
                        </Badge>
                      </div>
                      <h4 className="font-semibold text-sm text-[#262263] dark:text-slate-100 mb-1">{i.title}</h4>
                      <div className="grid grid-cols-2 gap-2 text-[11px] text-[#655dc6]/70 dark:text-slate-400">
                        <div>{T('تاريخ التقديم:', 'Submitted:')} <span className="font-medium text-slate-700 dark:text-slate-200">{fmt(i.submitted_at)}</span></div>
                        <div>{T('آخر تحديث:', 'Last update:')} <span className="font-medium text-slate-700 dark:text-slate-200">{fmt(i.last_update_at)}</span></div>
                        {i.assignee?.name && (
                          <div className="col-span-2">{T('المسؤول:', 'Assignee:')} <span className="font-medium text-slate-700 dark:text-slate-200">{i.assignee.name}</span></div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* New incident dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto dark:bg-slate-900 dark:border-slate-700" dir={direction} data-testid="new-incident-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 dark:text-slate-100">
              <Siren size={20} weight="duotone" className="text-rose-600 dark:text-rose-400" />
              {T('بلاغ جديد', 'New incident report')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="dark:text-slate-200">{T('تصنيف البلاغ', 'Category')}</Label>
                <Select value={form.category_key} onValueChange={(v) => setForm({ ...form, category_key: v })}>
                  <SelectTrigger className="bg-[#E8E6F5] dark:bg-slate-800 border-[#C4BFF0] dark:border-slate-700 dark:text-slate-100" data-testid="new-category-select">
                    <SelectValue placeholder={T('اختر تصنيفاً', 'Pick a category')} />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(c => (
                      <SelectItem key={c.key} value={c.key}>{T(c.name_ar, c.name_en)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="dark:text-slate-200">{T('مستوى الأهمية', 'Priority')}</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="bg-[#E8E6F5] dark:bg-slate-800 border-[#C4BFF0] dark:border-slate-700 dark:text-slate-100" data-testid="new-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRIORITY_META).map(([k, m]) => (
                      <SelectItem key={k} value={k}>{T(m.ar, m.en)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1">
              <Label className="dark:text-slate-200">{T('عنوان مختصر', 'Short title')}</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder={T('ملخص سريع عن الحادث', 'Quick summary')}
                className="w-full bg-[#E8E6F5] dark:bg-slate-800 border-[#C4BFF0] dark:border-slate-700 dark:text-slate-100 dark:placeholder:text-slate-500"
                data-testid="new-title"
                maxLength={140}
              />
            </div>

            <div className="space-y-1">
              <Label className="dark:text-slate-200">{T('وصف تفصيلي', 'Detailed description')}</Label>
              <Textarea
                rows={5}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder={T('اذكر ما حدث، الوقت التقريبي، والأشخاص المعنيين…', 'What happened, approximate time, people involved…')}
                className="bg-[#E8E6F5] dark:bg-slate-800 border-[#C4BFF0] dark:border-slate-700 dark:text-slate-100 dark:placeholder:text-slate-500 text-sm"
                data-testid="new-description"
                maxLength={4000}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="dark:text-slate-200">{T('تاريخ الحادث', 'Incident date')}</Label>
                <Input
                  type="date"
                  value={form.incident_date}
                  onChange={(e) => setForm({ ...form, incident_date: e.target.value })}
                  className="w-full bg-[#E8E6F5] dark:bg-slate-800 border-[#C4BFF0] dark:border-slate-700 dark:text-slate-100"
                  data-testid="new-date"
                />
              </div>
              <div className="space-y-1">
                <Label className="dark:text-slate-200">{T('الموقع (اختياري)', 'Location (optional)')}</Label>
                <Input
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  placeholder={T('مثل: الاستقبال', 'eg. Reception')}
                  className="w-full bg-[#E8E6F5] dark:bg-slate-800 border-[#C4BFF0] dark:border-slate-700 dark:text-slate-100 dark:placeholder:text-slate-500"
                  data-testid="new-location"
                  maxLength={140}
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label className="dark:text-slate-200">{T('شهود (اختياري)', 'Witnesses (optional)')}</Label>
              <Input
                value={form.witnesses}
                onChange={(e) => setForm({ ...form, witnesses: e.target.value })}
                placeholder={T('أسماء من شاهدوا الحادث', 'Names of people who witnessed')}
                className="w-full bg-[#E8E6F5] dark:bg-slate-800 border-[#C4BFF0] dark:border-slate-700 dark:text-slate-100 dark:placeholder:text-slate-500"
                data-testid="new-witnesses"
                maxLength={500}
              />
            </div>

            <div className="space-y-1">
              <Label className="flex items-center gap-1 dark:text-slate-200">
                <Paperclip size={14} /> {T(`مرفقات (حتى ${MAX_ATT})`, `Attachments (up to ${MAX_ATT})`)}
              </Label>
              <input
                type="file"
                multiple
                accept="image/*,application/pdf,video/mp4,video/quicktime"
                onChange={handleFileChange}
                disabled={attachments.length >= MAX_ATT}
                className="block w-full text-xs text-[#655dc6] dark:text-slate-300 file:me-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-[#E8E6F5] dark:file:bg-slate-800 file:text-[#655dc6] dark:file:text-violet-300 hover:file:bg-[#DAD5F0] dark:hover:file:bg-slate-700"
                data-testid="new-attachments-input"
              />
              {attachments.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {attachments.map((a, idx) => (
                    <span key={idx} className="inline-flex items-center gap-1.5 bg-white dark:bg-slate-800 border border-[#C4BFF0] dark:border-slate-700 dark:text-slate-200 text-[11px] rounded px-2 py-1" data-testid={`att-pill-${idx}`}>
                      <Paperclip size={12} />
                      <span className="max-w-[160px] truncate">{a.filename}</span>
                      <button type="button" onClick={() => removeAttachment(idx)} className="text-rose-600 dark:text-rose-400 hover:text-rose-700">
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)}>{T('إلغاء', 'Cancel')}</Button>
            <Button
              onClick={submitIncident}
              disabled={submitting}
              className="bg-rose-600 hover:bg-rose-700 text-white gap-1"
              data-testid="submit-incident-btn"
            >
              <PaperPlaneTilt size={14} />
              {submitting ? T('جارٍ الإرسال…', 'Submitting…') : T('إرسال البلاغ', 'Submit report')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog (read-only + comments) */}
      <Dialog open={Boolean(detailId)} onOpenChange={(v) => !v && closeDetail()}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto dark:bg-slate-900 dark:border-slate-700" dir={direction} data-testid="my-incident-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base dark:text-slate-100">
              <Siren size={20} weight="duotone" className="text-rose-600 dark:text-rose-400" />
              {inc?.reference || T('تفاصيل البلاغ', 'Details')}
            </DialogTitle>
          </DialogHeader>

          {!inc ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#655dc6]" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className={`${sm.cls} gap-1`}>
                  <sm.Icon size={12} weight="bold" />{T(sm.ar, sm.en)}
                </Badge>
                <Badge variant="outline" className={`${pm.cls} gap-1`}>
                  <pm.Icon size={12} weight="bold" />{T(pm.ar, pm.en)}
                </Badge>
                <Badge variant="outline" className="bg-[#E8E6F5] text-[#655dc6] border-[#C4BFF0] dark:bg-slate-800 dark:text-violet-300 dark:border-slate-700">
                  {T(inc.category?.name_ar, inc.category?.name_en)}
                </Badge>
              </div>

              <div className="rounded-lg border border-[#C4BFF0] dark:border-slate-700 p-3 bg-[#F8F6FF] dark:bg-slate-800/60">
                <h3 className="font-semibold text-[#262263] dark:text-slate-100 text-sm">{inc.title}</h3>
                <p className="text-xs text-[#655dc6]/80 dark:text-slate-300 mt-1 whitespace-pre-wrap">{inc.description}</p>
                <div className="grid grid-cols-2 gap-2 text-xs text-[#655dc6]/80 dark:text-slate-300 mt-3">
                  <div><span className="block text-[10px] text-[#655dc6]/60 dark:text-slate-400">{T('تاريخ الحادث', 'Incident date')}</span><span className="font-medium">{inc.incident_date || '—'}</span></div>
                  <div><span className="block text-[10px] text-[#655dc6]/60 dark:text-slate-400">{T('الموقع', 'Location')}</span><span className="font-medium">{inc.location || '—'}</span></div>
                  <div><span className="block text-[10px] text-[#655dc6]/60 dark:text-slate-400">{T('تاريخ التقديم', 'Submitted')}</span><span className="font-medium tabular-nums">{fmt(inc.submitted_at)}</span></div>
                  <div><span className="block text-[10px] text-[#655dc6]/60 dark:text-slate-400">{T('المسؤول الحالي', 'Current assignee')}</span><span className="font-medium">{inc.assignee?.name || T('قيد المراجعة من الإدارة', 'Being reviewed by admin')}</span></div>
                  {inc.resolution && (
                    <div className="col-span-2 pt-2 border-t border-[#C4BFF0] dark:border-slate-700 mt-1">
                      <span className="block text-[10px] text-[#655dc6]/60 dark:text-slate-400">{T('الإجراء المتخذ', 'Resolution')}</span>
                      <span className="font-medium whitespace-pre-wrap">{inc.resolution}</span>
                    </div>
                  )}
                </div>
              </div>

              {inc.attachments?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-xs font-semibold text-[#655dc6] dark:text-violet-300 flex items-center gap-1.5">
                    <Paperclip size={14} /> {T('المرفقات', 'Attachments')}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {inc.attachments.map((a, idx) => (
                      <a key={idx} href={`${API}/incidents/${inc.id}/attachment/${idx}`} target="_blank" rel="noreferrer"
                         className="inline-flex items-center gap-1.5 bg-[#E8E6F5] dark:bg-slate-800 border border-[#C4BFF0] dark:border-slate-700 text-[#655dc6] dark:text-violet-300 text-xs px-2 py-1 rounded hover:bg-[#DAD5F0] dark:hover:bg-slate-700">
                        <Download size={12} /> {a.filename}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Timeline */}
              <div className="space-y-2">
                <h4 className="text-xs font-semibold text-[#655dc6] dark:text-violet-300 flex items-center gap-1.5">
                  <ChatCircleText size={14} /> {T('مسار البلاغ', 'Timeline')}
                </h4>
                <div className="space-y-2" data-testid="my-incident-timeline">
                  {events.map((e) => {
                    const label = EVENT_LABELS[e.kind] || { ar: e.kind, en: e.kind };
                    return (
                      <div key={e.id} className="bg-white dark:bg-slate-800/60 border border-[#C4BFF0] dark:border-slate-700 rounded-lg p-3">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="text-xs font-semibold text-[#262263] dark:text-slate-100">{T(label.ar, label.en)}</span>
                          <span className="text-[10px] text-[#655dc6]/60 dark:text-slate-400 tabular-nums">{fmt(e.created_at)}</span>
                        </div>
                        <div className="text-[11px] text-[#655dc6]/80 dark:text-slate-300">
                          {T(`بواسطة ${e.by_user_name || '—'}`, `by ${e.by_user_name || '—'}`)}
                        </div>
                        {e.note && (
                          <p className="text-xs text-slate-700 dark:text-slate-200 mt-1 whitespace-pre-wrap">{e.note}</p>
                        )}
                        {e.kind === 'forwarded' && e.meta?.assignee_name && (
                          <p className="text-[11px] text-indigo-700 dark:text-indigo-300 mt-1">
                            → {e.meta.assignee_name}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="pt-2 border-t border-[#C4BFF0] dark:border-slate-700">
                  <Textarea
                    rows={2}
                    placeholder={T('اكتب تعليقاً أو معلومة إضافية…', 'Add a comment or extra info…')}
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    className="bg-[#E8E6F5] dark:bg-slate-800 border-[#C4BFF0] dark:border-slate-700 dark:text-slate-100 dark:placeholder:text-slate-500 text-sm"
                    data-testid="my-comment-input"
                  />
                  <div className="flex justify-end mt-2">
                    <Button size="sm" onClick={postComment} disabled={commenting || !commentText.trim()}
                            className="bg-[#655dc6] hover:bg-[#544cb0] text-white gap-1" data-testid="my-submit-comment-btn">
                      <ChatCircleText size={14} /> {T('إرسال', 'Post')}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
